import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@repo/videa-components/src/index';

import { useGetDateFNSLocale } from '@/locales/helpers';

import { useLatestImagesV2 } from '../../../../react-query/useLatestImagesv2';
import { usePatientVisits } from '../../../../react-query/usePatientVisits';
import { ImageCarousel } from '../ImageCarousel/ImageCarousel';

import type {
  SelectedVisit,
  TValidImageType,
  Visit,
  VisitAttachmentsTabProps,
  VisitImage,
  VisitWithId,
} from './visitAttachmentsTypes';
import { VALID_IMAGE_TYPES } from './visitAttachmentsTypes';
import { formatVisitDate } from './visitAttachmentsUtils';
import { VisitDropdown } from './VisitDropdown';
import { VisitTagsList } from './VisitTagsList';

const useVisitImages = (visits: Visit[], selectedVisits: SelectedVisit[]) => {
  const [allImages, setAllImages] = useState<VisitImage[]>([]);
  const [loadingVisits, setLoadingVisits] = useState<Set<number>>(new Set());
  const previousVisitRef = useRef<number | null>(null);

  const currentVisit = selectedVisits[selectedVisits.length - 1];
  const matchingVisit = visits.find((v) => v.id === currentVisit?.id);

  // Try to get videaPatientId from useGetVideaId first, fall back to visit data
  const videaPatientId = matchingVisit?.patientVideaId || '';

  const {
    data: latestImagesData,
    isSuccess: hasImagesLoaded,
    refetch,
    isLoading: isImagesLoading,
  } = useLatestImagesV2({
    videaPatientId,
    estimatedVisitId: currentVisit?.id.toString() || '',
    options: {
      enabled: !!currentVisit && !!videaPatientId,
      cacheTime: 5 * 60 * 1000, // 5 minutes
      staleTime: 0, // Always consider data stale
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    },
  });

  // Track loading state per visit
  useEffect(() => {
    if (currentVisit) {
      if (isImagesLoading) {
        setLoadingVisits((prev) => new Set([...prev, currentVisit.id]));
      } else {
        setLoadingVisits((prev) => {
          const newSet = new Set(prev);
          newSet.delete(currentVisit.id);
          return newSet;
        });
      }
    }
  }, [currentVisit, isImagesLoading]);

  // Only refetch when the visit actually changes
  useEffect(() => {
    if (currentVisit && currentVisit.id !== previousVisitRef.current) {
      if (previousVisitRef.current !== null) {
        // This is a visit change, not the initial load
        refetch();
      }
      previousVisitRef.current = currentVisit.id;
    }
  }, [currentVisit, refetch]);

  // Reset when no visits are selected
  useEffect(() => {
    if (selectedVisits.length === 0) {
      previousVisitRef.current = null;
      setLoadingVisits(new Set());
      // Clear all images when no visits are selected
      setAllImages([]);
    }
  }, [selectedVisits]);

  // Sync allImages with selectedVisits - remove images for visits that are no longer selected
  useEffect(() => {
    const selectedVisitIds = new Set(selectedVisits.map((v) => v.id));
    setAllImages((prev) => {
      const filteredImages = prev.filter((img) =>
        selectedVisitIds.has(img.visitId),
      );
      return filteredImages;
    });
  }, [selectedVisits]);

  useEffect(() => {
    if (!hasImagesLoaded || !latestImagesData?.analyses || !currentVisit)
      return;

    const newImages = latestImagesData.analyses
      .filter((image): image is typeof image & { imageType: TValidImageType } =>
        VALID_IMAGE_TYPES.includes(image.imageType as TValidImageType),
      )
      .map((image) => ({
        visitId: currentVisit.id,
        // Use sharpenedPath if available, otherwise fall back to path, then originalPath
        path: image.sharpenedPath || image.path || image.originalPath || '',
        imageType: image.imageType,
        analysisId: image.analysesId,
      }))
      .filter(
        (img): img is VisitImage =>
          typeof img.path === 'string' &&
          img.path.length > 0 &&
          !isNaN(img.visitId),
      );

    setAllImages((prev) => {
      const filteredImages = prev.filter(
        (img) => img.visitId !== currentVisit.id,
      );
      return [...filteredImages, ...newImages];
    });
  }, [hasImagesLoaded, latestImagesData, currentVisit]);

  return { allImages, setAllImages, loadingVisits };
};

export const VisitAttachmentsTab = ({
  patientId,
  videaId,
  onImagesChange,
  isOralinxV2 = false,
}: VisitAttachmentsTabProps) => {
  const {
    data: visitsData,
    isLoading,
    error,
  } = usePatientVisits({
    patientId: patientId || '',
    videaId,
  });

  const [selectedVisits, setSelectedVisits] = useState<SelectedVisit[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openAccordionItems, setOpenAccordionItems] = useState<Set<string>>(
    new Set(),
  );

  const dateLocale = useGetDateFNSLocale();

  const visits = useMemo(
    () =>
      (visitsData?.visits || [])
        .filter((v): v is VisitWithId => v.id !== null)
        .map((visit) => ({
          ...visit,
          imageTypeCounts: visit.imageTypeCounts.map((count) => ({
            type: count.type,
            count:
              typeof count.count === 'number'
                ? count.count
                : typeof count.count === 'string'
                  ? parseInt(count.count, 10)
                  : 0,
          })),
        })),
    [visitsData?.visits],
  );

  const {
    allImages: visitImages,
    setAllImages,
    loadingVisits,
  } = useVisitImages(visits, selectedVisits);

  useEffect(() => {
    if (onImagesChange) {
      const selectedImages = visitImages.map((img) => ({
        path: img.path,
        analysisId: img.analysisId,
      }));

      onImagesChange(selectedImages);
    }
  }, [visitImages, onImagesChange, selectedVisits]);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  const toggleVisitSelection = useCallback(
    (visit: Visit) => {
      setSelectedVisits((prev) => {
        const isSelected = prev.some((v) => v.id === visit.id);

        if (isSelected) {
          // Remove visit and close its accordion
          const newSelectedVisits = prev.filter((v) => v.id !== visit.id);

          if (visit.id !== null) {
            const visitId = visit.id.toString();
            setOpenAccordionItems((prevOpen) => {
              const newOpen = new Set(prevOpen);
              newOpen.delete(visitId);
              return newOpen;
            });
          }
          return newSelectedVisits;
        } else {
          const newVisit = {
            id: visit.id as number,
            date: formatVisitDate(visit.visitDate, dateLocale),
          };

          if (prev.some((v) => v.id === newVisit.id)) {
            return prev;
          }

          // Add visit and open its accordion
          if (visit.id !== null) {
            const visitId = visit.id.toString();
            setOpenAccordionItems((prevOpen) => {
              const newOpen = new Set(prevOpen);
              newOpen.add(visitId);
              return newOpen;
            });
          }

          return [...prev, newVisit];
        }
      });
    },
    [dateLocale],
  );

  const removeImage = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      const imageToRemove = visitImages[index];
      if (!imageToRemove) return;

      setAllImages((prevImages: VisitImage[]) =>
        prevImages.filter((img: VisitImage) => img.path !== imageToRemove.path),
      );

      const remainingImagesForVisit = visitImages.filter(
        (img) =>
          img.visitId === imageToRemove.visitId &&
          img.path !== imageToRemove.path,
      );

      if (remainingImagesForVisit.length === 0) {
        setSelectedVisits((prevVisits) =>
          prevVisits.filter((v) => v.id !== imageToRemove.visitId),
        );
      }
    },
    [visitImages, setAllImages],
  );

  const { t } = useLingui();

  return (
    <div className="vai-space-y-4">
      <h3 className="vai-text-sm vai-font-medium vai-mb-2">
        {isOralinxV2 ? (
          <Trans>Select visits to attach images (optional)</Trans>
        ) : (
          <Trans>Select visits to attach images</Trans>
        )}
      </h3>

      {isLoading && (
        <div className="vai-p-4 vai-text-center vai-text-gray-500">
          <Trans>Loading visits...</Trans>
        </div>
      )}

      {!isLoading && error !== null && error !== undefined && (
        <div className="vai-p-4 vai-text-center vai-text-red-500">
          <Trans>Error loading visits. Please try again.</Trans>
        </div>
      )}

      {!isLoading && !error && visits.length === 0 && (
        <div className="vai-p-4 vai-text-center vai-text-gray-500">
          <Trans>No visits available for this patient.</Trans>
        </div>
      )}

      {visits.length > 0 && (
        <>
          <VisitDropdown
            visits={visits}
            selectedVisits={selectedVisits.map((visit) => visit.id)}
            isOpen={isDropdownOpen}
            onToggle={toggleDropdown}
            onToggleVisitSelection={toggleVisitSelection}
            isLoading={isLoading}
          />

          <VisitTagsList
            selectedVisits={selectedVisits}
            visits={visits}
            onRemoveVisit={toggleVisitSelection}
          />

          <Accordion
            type="multiple"
            value={Array.from(openAccordionItems)}
            onValueChange={(value) => setOpenAccordionItems(new Set(value))}
          >
            {selectedVisits.length > 2 && (
              <div className="vai-text-xs vai-text-gray-500 vai-text-center vai-pb-2 vai-italic">
                <Trans>Scroll down to see all selected visits</Trans>
              </div>
            )}
            <div className="vai-space-y-3 vai-max-h-[500px] vai-overflow-y-auto vai-pr-2">
              {selectedVisits.map((visit) => {
                const filteredImages = visitImages.filter(
                  (img: VisitImage) => img.visitId === visit.id,
                );
                const bwImages = filteredImages.filter(
                  (img: VisitImage) => img.imageType === 'BITEWING',
                );
                const paImages = filteredImages.filter(
                  (img: VisitImage) => img.imageType === 'PERIAPICAL',
                );
                const panoImages = filteredImages.filter(
                  (img: VisitImage) => img.imageType === 'PANORAMIC',
                );

                return (
                  <AccordionItem
                    value={visit.id.toString()}
                    key={visit.id}
                    className="vai-border vai-border-gray-200 vai-rounded-lg vai-shadow-sm"
                  >
                    <AccordionTrigger className="vai-bg-gray-50 vai-rounded-t-lg vai-p-3 vai-shadow-sm hover:vai-shadow-md vai-transition-shadow">
                      <div className="vai-flex vai-items-center vai-gap-2 vai-w-full">
                        <h4 className="vai-text-sm vai-font-medium">
                          {visit.date}
                        </h4>
                        {loadingVisits.has(visit.id) && (
                          <div className="vai-animate-spin vai-rounded-full vai-h-4 vai-w-4 vai-border-b-2 vai-border-sapphire-600"></div>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="vai-space-y-4 vai-p-4 vai-bg-white vai-rounded-b-lg">
                      {loadingVisits.has(visit.id) && (
                        <div className="vai-flex vai-items-center vai-justify-center vai-py-8">
                          <div className="vai-flex vai-flex-col vai-items-center vai-gap-2">
                            <div className="vai-animate-spin vai-rounded-full vai-h-6 vai-w-6 vai-border-b-2 vai-border-sapphire-600"></div>
                            <span className="vai-text-sm vai-text-gray-500">
                              <Trans>Loading images...</Trans>
                            </span>
                          </div>
                        </div>
                      )}

                      {!loadingVisits.has(visit.id) && bwImages.length > 0 && (
                        <div className="vai-space-y-2">
                          <h5 className="vai-text-sm vai-font-medium vai-text-gray-700">
                            <Trans>Bitewings</Trans>
                          </h5>
                          <ImageCarousel
                            images={bwImages.map((img: VisitImage) => ({
                              path: img.path,
                              originalIndex: visitImages.findIndex(
                                (v) => v.path === img.path,
                              ),
                            }))}
                            onRemoveImage={removeImage}
                            title={t`Bitewing Images` + ` (${bwImages.length})`}
                            isCollapsible
                          />
                        </div>
                      )}

                      {!loadingVisits.has(visit.id) && paImages.length > 0 && (
                        <div className="vai-space-y-2">
                          <h5 className="vai-text-sm vai-font-medium vai-text-gray-700">
                            <Trans>Periapicals</Trans>
                          </h5>
                          <ImageCarousel
                            images={paImages.map((img: VisitImage) => ({
                              path: img.path,
                              originalIndex: visitImages.findIndex(
                                (v) => v.path === img.path,
                              ),
                            }))}
                            onRemoveImage={removeImage}
                            title={
                              t`Periapical Images` + ` (${paImages.length})`
                            }
                            isCollapsible
                          />
                        </div>
                      )}

                      {!loadingVisits.has(visit.id) &&
                        panoImages.length > 0 && (
                          <div className="vai-space-y-2">
                            <h5 className="vai-text-sm vai-font-medium vai-text-gray-700">
                              <Trans>Panoramics</Trans>
                            </h5>
                            <ImageCarousel
                              images={panoImages.map((img: VisitImage) => ({
                                path: img.path,
                                originalIndex: visitImages.findIndex(
                                  (v) => v.path === img.path,
                                ),
                              }))}
                              onRemoveImage={removeImage}
                              title={
                                t`Panoramic Images` + ` (${panoImages.length})`
                              }
                              isCollapsible
                            />
                          </div>
                        )}

                      {!loadingVisits.has(visit.id) &&
                        bwImages.length === 0 &&
                        paImages.length === 0 &&
                        panoImages.length === 0 && (
                          <div className="vai-text-center vai-py-8 vai-text-gray-500">
                            <Trans>No images found for this visit.</Trans>
                          </div>
                        )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </div>
          </Accordion>
        </>
      )}
    </div>
  );
};
